<template>
  <div :class="containerClass">
    <page-header :title="this.$route.params.id ? 'Edit FAQ' : 'Add New FAQ'" :container-class="null" class="mb-16pt" />
    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- FAQ Type Selection (only for new program FAQs) -->
      <div v-if="!$route.params.id && $route.params.programId" class="mb-4">
        <b-form-group label="FAQ Type" label-class="form-label">
          <b-form-radio-group
            v-model="faqType"
            :options="[
              { text: 'Custom', value: 'custom' },
              { text: 'Default', value: 'default' },
            ]"
            @change="onFaqTypeChange"
            class="mb-3"
          ></b-form-radio-group>
        </b-form-group>

        <!-- Default FAQ Selection -->
        <div v-if="faqType === 'default'" class="mb-4">
          <label class="form-label">Select Default FAQ</label>
          <b-skeleton-wrapper :loading="loadingDefaultFaqs">
            <template #loading>
              <div v-for="item in [1, 2, 3]" :key="item" class="card mb-2">
                <div class="card-body">
                  <b-skeleton width="85%"></b-skeleton>
                  <b-skeleton width="60%" class="mt-1"></b-skeleton>
                </div>
              </div>
            </template>

            <div v-if="defaultFaqs.length">
              <div
                v-for="faq in defaultFaqs"
                :key="faq.id"
                class="default-faq-option"
                :class="{ selected: selectedDefaultFaq && selectedDefaultFaq.id === faq.id }"
                @click="onDefaultFaqSelect(faq)"
              >
                <div class="default-faq-content">
                  <div class="default-faq-question">{{ faq.question }}</div>
                  <div class="default-faq-answer">
                    {{ faq.answer.substring(0, 80) }}{{ faq.answer.length > 80 ? '...' : '' }}
                  </div>
                </div>
                <div v-if="selectedDefaultFaq && selectedDefaultFaq.id === faq.id" class="default-faq-check">
                  <i class="fas fa-check"></i>
                </div>
              </div>
            </div>
            <div v-else-if="!loadingDefaultFaqs">
              <p class="text-muted">No FAQs available.</p>
            </div>
          </b-skeleton-wrapper>
        </div>
      </div>

      <b-form-group
        v-if="['program-faqs-add', 'program-faqs-edit'].includes($route.name)"
        label="Program Category"
        label-class="form-label"
      >
        <v-select
          id="program-category"
          class="form-control v-select-custom"
          label="title"
          v-model="transition_program_id"
          :reduce="program => program.id"
          placeholder="Select program category"
          :options="transProgramsOptions"
          :loading="areTransProgramsLoading"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.title }}
          </template>
        </v-select>
      </b-form-group>

      <b-form-group label="Question" label-for="question" label-class="form-label">
        <b-form-input
          id="question"
          placeholder="Enter Question"
          v-model.trim="question"
          :disabled="isFormLoading"
          :state="!$v.question.required && $v.question.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Answer" label-for="answer" label-class="form-label">
        <b-form-textarea
          class="form-control"
          rows="10"
          placeholder="Please write your answer here."
          v-model.trim="answer"
          :disabled="isFormLoading"
          :state="!$v.answer.required && $v.answer.$dirty ? false : null"
        ></b-form-textarea>
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>
      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { required } from 'vuelidate/lib/validators';
import PageHeader from '@/components/Ui/PageHeader.vue';
import vSelect from 'vue-select';

export default {
  extends: Page,
  components: { PageHeader, vSelect },
  data() {
    return {
      title: this.$route.params.id ? 'Edit FAQ' : 'Add FAQ',
      question: '',
      answer: '',
      isLoading: false,
      faqType: 'custom', // 'default' or 'custom'
      defaultFaqs: [],
      selectedDefaultFaq: null,
      loadingDefaultFaqs: false,
      linkedFaqs: [],
      transition_program_id: null,
      transProgramsOptions: [],
      areTransProgramsLoading: false,
    };
  },
  validations() {
    return {
      question: { required },
      answer: { required },
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isFormLoading() {
      return this.isLoading;
    },
    isDefaultFaq() {
      // Check if this is a default FAQ route (no programId in params)
      return !this.$route.params.programId;
    },
    breadcrumb() {
      const crumbs = [{ text: this.$t('home'), to: this.routes.home }];

      if (this.$route.params.programId) {
        crumbs.push(
          { text: 'Programs', to: { name: 'manage-programs' } },
          {
            text: 'Manage FAQs',
            to: { name: 'prg-faq-management-list', params: { programId: this.$route.params.programId } },
          }
        );
      } else {
        crumbs.push({ text: 'Program FAQs', to: { name: 'program-faqs' } });
      }

      crumbs.push({ text: this.$route.params.id ? 'Edit FAQ' : 'Add FAQ', active: true });
      return crumbs;
    },
  },
  methods: {
    ...mapActions('program', [
      'createProgramFaq',
      'updateProgramFaq',
      'getProgramFaq',
      'createDefaultProgramFaq',
      'updateDefaultProgramFaq',
      'getDefaultProgramFaq',
      'getDefaultProgramFaqs',
      'getAllProgramFaqs',
    ]),
    ...mapActions('school', ['getAllTransPrograms']),
    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        if (
          !this.$route.params.id &&
          this.$route.params.programId &&
          this.isDuplicateQuestion(this.question)
        ) {
          this.makeToast({
            variant: 'warning',
            msg: 'A FAQ with this question is already attached to the program.',
          });
          this.isLoading = false;
          return;
        }
        try {
          const data = {
            question: this.question,
            answer: this.answer,
            school: this.getLoggedInUser.linked_entity.id,
            ...(this.$route.params.programId ? { program: this.$route.params.programId } : {}),
            ...(this.transition_program_id ? { transition_program_id: this.transition_program_id } : {}),
          };

          if (this.$route.params.id) {
            // Update existing FAQ
            if (this.isDefaultFaq) {
              await this.updateDefaultProgramFaq({
                id: this.$route.params.id,
                data,
              });
            } else {
              await this.updateProgramFaq({
                id: this.$route.params.id,
                data,
              });
            }
            this.makeToast({ variant: 'success', msg: this.$t('FAQ Updated!') });
          } else {
            // Create new FAQ
            if (this.isDefaultFaq) {
              await this.createDefaultProgramFaq(data);
            } else {
              await this.createProgramFaq(data);
            }
            this.makeToast({ variant: 'success', msg: 'FAQ Added!' });
          }

          const redirectRoute = this.$route.params.programId
            ? {
                name: 'prg-faq-management-list',
                params: { programId: this.$route.params.programId },
              }
            : { name: 'program-faqs' };

          setTimeout(() => this.$router.push(redirectRoute), 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },

    async fetchDefaultFaqs() {
      this.loadingDefaultFaqs = true;
      try {
        const resp = await this.getDefaultProgramFaqs({
          ...(this.$route.params.programId ? { program: this.$route.params.programId } : {}),
        });
        this.defaultFaqs = resp.data || [];
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.loadingDefaultFaqs = false;
    },

    onFaqTypeChange() {
      if (this.faqType === 'default' && this.defaultFaqs.length === 0) {
        this.fetchDefaultFaqs();
      }
      // Clear fields when switching types
      this.question = '';
      this.answer = '';
      this.selectedDefaultFaq = null;
    },

    isDuplicateQuestion(questionText) {
      return this.linkedFaqs.some(
        (f) => f.question.trim().toLowerCase() === questionText.trim().toLowerCase()
      );
    },

    onDefaultFaqSelect(faq) {
      if (this.isDuplicateQuestion(faq.question)) {
        this.makeToast({
          variant: 'warning',
          msg: 'This FAQ is already attached to the program.',
        });
        return;
      }
      this.selectedDefaultFaq = faq;
      this.question = faq.question;
      this.answer = faq.answer;
    },
    async fetchTransPrograms() {
      this.areTransProgramsLoading = true;
      try {
        const response = await this.getAllTransPrograms({});
        this.transProgramsOptions = response.data || [];
      } catch (e) {
        this.transProgramsOptions = [];
      }
      this.areTransProgramsLoading = false;
    },
  },
  async mounted() {
    this.isLoading = true;
    this.fetchTransPrograms();

    if (this.$route.params.programId) {
      try {
        const resp = await this.getAllProgramFaqs({ program: this.$route.params.programId });
        this.linkedFaqs = resp.data || [];
      } catch (_) {
        this.linkedFaqs = [];
      }
    }

    try {
      if (this.$route.params.id) {
        let resp;
        if (this.isDefaultFaq) {
          resp = (await this.getDefaultProgramFaq(this.$route.params.id)).data;
        } else {
          resp = (await this.getProgramFaq(this.$route.params.id)).data;
        }
        this.question = resp.question;
        this.answer = resp.answer;
        this.transition_program_id =
          resp.transition_program_id || resp.transition_program?.id || resp.transition_program || null;
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      const fallbackRoute = this.$route.params.programId
        ? {
            name: 'prg-faq-management-list',
            params: { programId: this.$route.params.programId },
          }
        : { name: 'program-faqs' };
      this.$router.push(fallbackRoute);
    }

    this.isLoading = false;
  },
};
</script>

<style scoped>
.default-faq-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-bottom: 8px;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fff;
}

.default-faq-option:hover {
  border-color: #4c9aff;
  box-shadow: 0 2px 8px rgba(76, 154, 255, 0.1);
  transform: translateY(-1px);
}

.default-faq-option.selected {
  border-color: #4c9aff;
  background-color: #f0f8ff;
  box-shadow: 0 2px 8px rgba(76, 154, 255, 0.15);
}

.default-faq-content {
  flex: 1;
  min-width: 0;
}

.default-faq-question {
  font-weight: 600;
  font-size: 14px;
  color: #1f2d3d;
  margin-bottom: 4px;
  line-height: 1.4;
}

.default-faq-answer {
  font-size: 12px;
  color: #6c7a89;
  line-height: 1.3;
}

.default-faq-check {
  margin-left: 12px;
  color: #4c9aff;
  font-size: 16px;
  flex-shrink: 0;
}

.default-faq-check i {
  animation: checkmark 0.3s ease-in-out;
}

@keyframes checkmark {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
